import { useEffect } from 'react'
import { createDbgLogger } from 'utils/dbgLogger'

type FREQUENCY = 'fast' | 'normal' | 'slow' | 'paused'

interface CreatePeriodicRefresherOpt {
  /**
   * trigger frequency when website/tab is presented
   */
  frequency?: FREQUENCY

  /**
   * trigger frequency when website/tab is not visible
   */
  tabBlurFrequency?: FREQUENCY

  /**
   * retry inteval when error thrown in refreshFn
   */
  retryMsDelay?: number

  /**
   * enables `console.debug()` to log down each start and end of `refreshFn()`, should be turned off after debugging
   */
  verbose?: boolean

  /**
   * name of this fresher to identify for debugging purpose
   */
  dbgName?: string
}
type AwaitingRefresh = [resolve: () => void, reject: (error: any) => void]

const FREQUENCY_TO_INTERVAL: Record<FREQUENCY, number> = {
  fast: parseInt(process.env.REACT_APP_FAST_REFRESH),
  normal: parseInt(process.env.REACT_APP_NORMAL_REFRESH),
  slow: parseInt(process.env.REACT_APP_SLOW_REFRESH),
  paused: Infinity,
}
const RUN_LATER_THRESHOLD_MS = 50
const DEFAULT_RETRY_MS_DELAY = 5000

/**
 * create a effect hook that triggers `refreshFn()` periodically
 *
 * * duplicated setInterval/loop is prevented if the effect hook is called multiple times across react tree
 * * will not trigger `refreshFn()` if no components are mounted calling the effect hook
 *
 * for exmple:
 *
 * ```
 * const useCoreFarmsPeriodicFetching = createPeriodicRefresher(
 *   async () => {
 *     await appDispatchAsyncThunk(fetchFarmsPublicDataAsync(coreFarmPids))
 *   },
 *   {
 *     frequency: 'slow',
 *     dbgName: 'CoreFarmsPublicDataPeriodicFetching',
 *     // verbose: true,
 *   },
 * )
 *
 * const App = () => {
 *   useCoreFarmsPeriodicFetching()
 * }
 * ```
 *
 * ## `.untilRefreshedOnce()`
 *
 * returns a Promise that
 * * resolves directly if `refreshFn()` is triggered and completed already
 * * trigger `refreshFn()` and resolves when it is completed
 *
 * for example:
 *
 * ```
 * const getLoadedCoreFarms = async (pid: number): Promise<DeserializedFarm> => {
 *   await useCoreFarmsPeriodicFetching.untilRefreshedOnce()
 *   return reduxStore.getState().farms.filter(
 *     // is core farm
 *   )
 * }
 * ```
 *
 * ## manual trigger `refreshFn()`
 *
 * call `useXXX.runRefresh()`, it returns a Promise that wait for `refreshFn()` to complete
 */
const createPeriodicRefresher = (refreshFn: () => any, opt?: CreatePeriodicRefresherOpt) => {
  const frequencyMs = FREQUENCY_TO_INTERVAL[opt?.frequency || 'normal']
  const tabBlurFrequencyMs = FREQUENCY_TO_INTERVAL[opt?.tabBlurFrequency || 'paused']
  const logPrefix = opt?.dbgName ? `${opt?.dbgName} ` : ''
  const retryMsDelay = opt?.retryMsDelay ?? DEFAULT_RETRY_MS_DELAY
  const dbgLog = createDbgLogger(opt?.verbose)

  let isTabBlur = false
  let mountedCounts = 0
  let lastRefreshedAt: number = null
  let nextJob: NodeJS.Timeout = null
  let running = 0
  let lastJobFailed = false

  const getFrequency = () => {
    if (lastJobFailed) return retryMsDelay
    return isTabBlur ? tabBlurFrequencyMs : frequencyMs
  }

  const scheduleNextJob = (intervalToNext: number) => {
    clearTimeout(nextJob)
    if (intervalToNext === Infinity) return

    nextJob = setTimeout(loop, intervalToNext)
  }

  const runRefresh = async () => {
    lastJobFailed = false
    try {
      running++
      dbgLog(`${logPrefix}refreshing...`)

      const result = await refreshFn()

      dbgLog(`${logPrefix}done`)
      flushAwaitingRefreshes()
      return result
    } catch (error) {
      console.error(`${logPrefix}error:`, error)
      flushAwaitingRefreshes(error)
      lastJobFailed = true
      throw error
    } finally {
      running--

      lastRefreshedAt = Date.now()
      scheduleNextJob(getFrequency())
    }
  }

  const loop = async () => {
    if (mountedCounts <= 0 || running) return

    if (lastRefreshedAt) {
      const intervalToNext = lastRefreshedAt + getFrequency() - Date.now()
      if (intervalToNext > RUN_LATER_THRESHOLD_MS) {
        scheduleNextJob(intervalToNext)
        return
      }
    }

    runRefresh()
  }

  window.addEventListener('visibilitychange', () => {
    isTabBlur = document.hidden
    loop()
  })

  const usePeriodicRefresh = (enabled = true) => {
    useEffect(() => {
      if (enabled) {
        mountedCounts++
        loop()
        return () => {
          mountedCounts--
        }
      }
      return () => undefined
    }, [enabled])
  }

  const awaitingRefreshes: AwaitingRefresh[] = []
  const untilRefreshedOnce = () =>
    new Promise<void>((resolve, reject) => {
      if (lastRefreshedAt) {
        resolve()
        return
      }
      mountedCounts++
      awaitingRefreshes.push([
        () => {
          mountedCounts--
          resolve()
        },
        reject,
      ])
      loop()
    })
  const flushAwaitingRefreshes = (error?: Error) => {
    if (!error) {
      while (awaitingRefreshes.length > 0) {
        awaitingRefreshes.shift()[0]()
      }
    } else {
      while (awaitingRefreshes.length > 0) {
        awaitingRefreshes.shift()[1](error)
      }
    }
  }

  usePeriodicRefresh.runRefresh = runRefresh
  usePeriodicRefresh.untilRefreshedOnce = untilRefreshedOnce

  return usePeriodicRefresh
}

export default createPeriodicRefresher
